import React from 'react'
import { Link } from 'wouter'
import { ChevronRight, Home } from 'lucide-react'

interface BreadcrumbItem {
  label: string
  href?: string
  isActive?: boolean
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm text-muted-foreground mb-6">
      {/* Home link */}
      <Link href="/dashboard" className="flex items-center hover:text-foreground transition-colors" aria-label="Home">
        <Home className="w-4 h-4" />
      </Link>
      
      {items.length > 0 && <ChevronRight className="w-4 h-4" />}
      
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {item.href && !item.isActive ? (
            <Link href={item.href} className="hover:text-foreground transition-colors">
              {item.label}
            </Link>
          ) : (
            <span 
              className={item.isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}
              aria-current={item.isActive ? 'page' : undefined}
            >
              {item.label}
            </span>
          )}
          
          {index < items.length - 1 && <ChevronRight className="w-4 h-4" />}
        </React.Fragment>
      ))}
    </nav>
  )
}

// Helper function to generate breadcrumbs based on current path
export function getBreadcrumbsForPath(pathname: string): BreadcrumbItem[] {
  const pathSegments = pathname.split('/').filter(Boolean)
  
  if (pathSegments.length === 0 || pathname === '/dashboard') {
    return [{ label: 'Dashboard', isActive: true }]
  }

  const breadcrumbs: BreadcrumbItem[] = []

  // Map routes to user-friendly labels
  const routeLabels: Record<string, string> = {
    'dashboard': 'Dashboard',
    'stores': 'Store Performance',
    'products': 'Product Analysis',
    'inventory': 'Inventory Management',
    'data-quality': 'Data Quality',
    'revenue-detail': 'Revenue Deep Dive',
    'units-detail': 'Units Deep Dive',
    'skus-detail': 'SKUs Deep Dive',
  }

  // Build breadcrumb hierarchy
  pathSegments.forEach((segment, index) => {
    const isLast = index === pathSegments.length - 1
    const label = routeLabels[segment] || segment
    
    if (isLast) {
      breadcrumbs.push({ label, isActive: true })
    } else {
      const href = '/' + pathSegments.slice(0, index + 1).join('/')
      breadcrumbs.push({ label, href })
    }
  })

  // Special handling for deep dive pages - show they're under Analytics
  const deepDivePages = ['revenue-detail', 'units-detail', 'skus-detail']
  if (deepDivePages.includes(pathSegments[0])) {
    return [
      { label: 'Analytics', href: '/dashboard' },
      { label: routeLabels[pathSegments[0]], isActive: true }
    ]
  }

  return breadcrumbs
}
